import React, { ReactElement } from 'react';
import {
  Component,
  Position,
  PuzzleInput,
  PuzzleToggle
} from "puzzle";
import CancelSelectedOnlySubmissionSection from './CancelSelectedOnlySubmissionSection';
import { createDataTree, TreeListExpandChangeEvent, TreeListRowClickEvent } from "@progress/kendo-react-treelist";
import _ from 'lodash';
import {
  BATCH_LANGUAGE,
  DATA_TREE_OBJECT,
  FILE,
  FILES_GRID_CUSTOM_PROPS,
  JOB,
  JOB_SELECTION_INFO,
  JSON_POST_DATA,
  SUBMISSION,
  SUBMISSION_DATA,
  TREE_LIST_ITEM
} from "./Types";
import { Push } from '@progress/kendo-react-animation'
import CancelSubmissionFilesGrid from './CancelSubmissionFilesGrid';
import { CheckboxChangeEvent } from "@progress/kendo-react-inputs";
import { FilterDescriptor } from "@progress/kendo-data-query";

export type PROPS = {
  cancelSubmissionData: any
};

export type STATE = {
  activeTabLanguages: boolean,
  activeTab: string,
  selectionInfo: Record<string, JOB_SELECTION_INFO>[],
  numberOfSelectedFiles: number[],
  filesGridDisplayed: boolean,
  filesGridProps?: FILES_GRID_CUSTOM_PROPS,
  filter: FilterDescriptor[],
  expanded: (string | number)[][],
  fileNameInputText: string,
  showMoreFilesJobInfo: number,
  jobSelectionInfo: Record<string, JOB_SELECTION_INFO>
}

class CancelSelectedOnly extends Component<PROPS, STATE> {
  public state: STATE = {
    activeTabLanguages: true,
    activeTab: 'language',
    selectionInfo: [],
    numberOfSelectedFiles: [],
    filesGridDisplayed: false,
    filter: [],
    expanded: [],
    fileNameInputText: '',
    showMoreFilesJobInfo: 0,
    jobSelectionInfo: {}
  };

  filesGridSubmissionIndex: number = 0;
  filesGridJobId: string = '';
  /**
   * Array of expanded items in language grouping
   */
  expandedLanguages: (string | number)[][] = [];
  /**
   * Array of expanded items in batch grouping
   */
  expandedBatches: (string | number)[][] = [];
  /**
   * Mapped Data Tree for grouping by Language
   */
  dataTreeForLanguage: any[] = [];
  /**
   * Mapped Data Tree for grouping by Batch
   */
  dataTreeForBatch: any[] = [];
  /**
   * Important SubmissionData for every submission
   */
  submissions: SUBMISSION_DATA[] = [];
  /**
   * Max number of files to display by a job. No files will be shown if filesCount is larger then this number
   */
  maxFilesPerJob: number = 5;

  /**
   * Initially creates mapped data, and selected arrays for all submissions
   * Selected array stores into state, since only that can be changed
   */
  moduleDidMount = () => {
    const { cancelSubmissionData } = this.props;
    if (cancelSubmissionData) {
      const submissions = _.isArray(cancelSubmissionData) ? cancelSubmissionData : [cancelSubmissionData];
      const selectionInfo: Record<string, JOB_SELECTION_INFO>[] = [];
      const expanded: (number | string)[][] = [];
      const numberOfSelectedFiles: number[] = [];
      this.submissions = submissions.map(this.mapSubmissionsOnInit.bind(this, selectionInfo, expanded));
      this.expandedBatches = [...expanded];
      this.expandedLanguages = [...expanded];
      this.setState({ selectionInfo, expanded, numberOfSelectedFiles });
    }
  };

  /**
   *  Adds file to both 'jobsAndFiles' arrays for batch and language
   */
  addFileToGlobalDataArray = (jobIndex: number, submission: SUBMISSION_DATA, file: FILE) => {
    const { targetId, documentName } = file;
    const { jobsAndFilesForLang, jobsAndFilesForBatch, jobs } = submission;
    const { jobId, targetIds, batchName, languageLocale } = jobs[jobIndex];
    targetIds.push(targetId);
    const fileDataObject = this.createObjectForDataTree('file', targetId, jobId, null, documentName);
    jobsAndFilesForLang.push({ ...fileDataObject, grandParentId: languageLocale });
    jobsAndFilesForBatch.push({ ...fileDataObject, grandParentId: batchName });
  };

  /**
   *  Adds job to both 'jobsAndFiles' arrays for batch and language
   * @param submissionData      Used to store relevant data about submission, and to create dataTrees
   * @param languagesAdded      Used to determine if job's language has been already created for firstLevel dataItem
   * @param batchesAdded        Used to determine if job's batchName has been already created for firstLevel dataItem
   * @param jobSelectionInfos   Used to manage selection of files in both treeList and grid
   * @param job                 Job data
   */
  addJobToGlobalDataArray = (submissionData: SUBMISSION_DATA, languagesAdded: Record<string, boolean>, batchesAdded: Record<string, boolean>, jobSelectionInfos: Record<string, JOB_SELECTION_INFO>, job: JOB) => {
    const { languages, batches, jobsAndFilesForLang, jobsAndFilesForBatch, jobs } = submissionData;
    const { targetLanguage, batchName, filesCount, files = [] } = job || {};
    const { locale = '', value: langName = '' } = targetLanguage || {};

    if (!languagesAdded[locale]) {
      languagesAdded[locale] = true;
      languages.push(this.createObjectForDataTree('firstLevel', locale, null, null, langName, 'batches', 'files'));
    }
    if (!batchesAdded[batchName]) {
      batchesAdded[batchName] = true;
      batches.push(this.createObjectForDataTree('firstLevel', batchName, null, null, batchName, 'languages', 'files'));
    }
    const jobId = this.createJobId(batchName, locale);

    const jobDataItemForBatch = this.createObjectForDataTree('job', jobId, batchName, null, langName, 'files', '', filesCount);
    jobsAndFilesForBatch.push({ ...jobDataItemForBatch });
    jobsAndFilesForLang.push({ ...jobDataItemForBatch, name: batchName, parentId: locale });

    jobs.push({
      batchName,
      filesCount,
      jobId,
      languageLocale: locale,
      languageValue: langName,
      targetIds: []
    });

    const lastJobIndex = jobs.length - 1;
    // If job has more files then maxFilesPerJob -> only add first $maxFilesPerJob number of files
    if (filesCount <= this.maxFilesPerJob) {
      files.forEach(this.addFileToGlobalDataArray.bind(this, lastJobIndex, submissionData));
    } else {
      let i: number;
      for (i = 0; i < this.maxFilesPerJob; i++) {
        this.addFileToGlobalDataArray(lastJobIndex, submissionData, files[i]);
      }
      const lastFileIndex = jobsAndFilesForLang.length - 1;
      jobsAndFilesForBatch[lastFileIndex].hasMoreFiles = true;
      jobsAndFilesForLang[lastFileIndex].hasMoreFiles = true;
    }
    jobSelectionInfos[jobId] = {
      targetsSelected: [],
      jobTargets: [...jobs[lastJobIndex].targetIds],
      jobSelected: false,
      filesCount
    };
  };

  /**
   *  Adds files target ids to targetSelected array
   *  @param submissionIndex
   *  @param targetId
   */
  addTargetIds = (submissionIndex: number, targetId: number) => {
    const selectionInfo = [...this.state.selectionInfo];
    const jobInfo = selectionInfo[submissionIndex][this.filesGridJobId];
    if (!_.includes(jobInfo.targetsSelected, targetId)) {
      jobInfo.targetsSelected.push(targetId);
    }
  }

  /**
   * Creates id for a job in dataTree arrays
   */
  createJobId = (batchName: string, targetLanguageLocale: string) => batchName + '-' + targetLanguageLocale;

  /**
   * Creates data for post request
   * Gets all jobs, and all document ids, that doesnt belong to those jobs, from selected array
   * @param selectionInfo info about selection in submission
   * @param submission  data of current submission
   */
  createJsonDataForPost = (selectionInfo: Record<string, JOB_SELECTION_INFO>, submission: SUBMISSION_DATA): JSON_POST_DATA => {
    const { jobs } = submission;
    const batchLanguageList: BATCH_LANGUAGE[] = [];
    const documentIds: number[] = [];
    const targetIds: number[] = [];

    let batchName, targetLanguage, jobId, jobSelectionInfo;
    jobs.forEach((job) => {
      batchName = job.batchName;
      targetLanguage = job.languageLocale;
      jobId = this.createJobId(batchName, targetLanguage);
      jobSelectionInfo = selectionInfo[jobId];
      if (jobSelectionInfo.jobSelected) batchLanguageList.push({ batchName, targetLanguage });
      else if (jobSelectionInfo.targetsSelected.length) targetIds.push(...jobSelectionInfo.targetsSelected)
    });
    const jsonDataForPost = {
      batchLanguageList,
      documentIds,
      targetIds
    };
    return jsonDataForPost;
  };

  /**
   *  Creates item for data tree mapping
   * @param type
   * @param id  ID of item
   * @param parentId        ID of it's parent
   * @param grandParentId   ID of parent's parent
   * @param name            Name of the item
   * @param childName       Name of it's children ('files', 'languages', 'batches' or null -> some values in columns are localised by those values)
   * @param grandChildName  Name of child's children ('files' or null  -> some values in columns are localised by those values)
   * @param filesCount      Total count of all Files beneath this item
   * @param targetId      id for post
   */
  createObjectForDataTree = (type: string, id: number | string, parentId: number | string | null, grandParentId: number | string | null, name: string, childName: string = '', grandChildName: string = '', filesCount?: number, targetId?: number): DATA_TREE_OBJECT => {
    return { type, id, parentId, grandParentId, name, childName, grandChildName, filesCount, targetId }
  };

  /**
   * Creates mapped data trees for both language and batch views,
   * Fills data object with reusable submission data
   */
  createSubmissionDataAndDataTrees = (submission: SUBMISSION, submissionSelectionInfo: Record<string, JOB_SELECTION_INFO>): SUBMISSION_DATA => {
    const { submissionName, submissionId, jobs } = submission;
    const languagesAdded = {};
    const batchesAdded = {};
    const data = {
      name: submissionName,
      submissionId,
      languages: [],
      batches: [],
      jobs: [],
      jobsAndFilesForBatch: [],
      jobsAndFilesForLang: []
    };

    jobs.forEach(this.addJobToGlobalDataArray.bind(this, data, languagesAdded, batchesAdded, submissionSelectionInfo));

    this.dataTreeForLanguage.push(createDataTree([...data.jobsAndFilesForLang, ...data.languages], i => i.id, i => i.parentId, 'subItems'));
    this.dataTreeForBatch.push(createDataTree([...data.jobsAndFilesForBatch, ...data.batches], i => i.id, i => i.parentId, 'subItems'));

    return data;
  };

  /**
   * Returns number of all selected files
   */
  getNumberOfSelectedFiles = () => this.state.selectionInfo.reduce(this.numberOfFilesReducer, 0);

  /**
   * Returns total number submission files
   */
  getTotalFilesCount = () => this.props.cancelSubmissionData.filesCount;

  /**
   *  Adds empty selected array that represents selected items of current submission
   *  Returns created Submission Data
   */
  mapSubmissionsOnInit = (selectionInfo: (Record<string, JOB_SELECTION_INFO>)[], expanded: (number | string)[][], submission: SUBMISSION): SUBMISSION_DATA => {
    const submissionSelectionInfo = {};
    selectionInfo.push(submissionSelectionInfo);
    expanded.push([]);
    return this.createSubmissionDataAndDataTrees(submission, submissionSelectionInfo);
  };

  /**
   * Counts number of selected files in all submissions
   */
  numberOfFilesReducer = (count: number, submissionSelectionInfo: Record<string, JOB_SELECTION_INFO>) => {
    const jobIds = Object.keys(submissionSelectionInfo);
    let len = jobIds.length;
    let jobInfo;
    while (len--) {
      jobInfo = submissionSelectionInfo[jobIds[len]];
      count += jobInfo.jobSelected ? jobInfo.filesCount : jobInfo.targetsSelected.length;
    }
    return count;
  };

  /**
   * Cancel button handler
   */
  onCancel = (): JSON_POST_DATA => {
    const { selectionInfo } = this.state;
    //Note: there should be added support for multiple submissions
    return this.createJsonDataForPost(selectionInfo[0], this.submissions[0]);
  };

  /**
   * RowClick and ExpandedColumnClick handler
   * Toggles expanded state of clicked dataItem
   */
  onExpandChange = (submissionIndex: number, e: TreeListRowClickEvent | TreeListExpandChangeEvent) => {
    const { id, expanded: itemExpanded } = e.dataItem;
    const { expanded } = this.state;

    expanded[submissionIndex] = itemExpanded ?
      expanded[submissionIndex].filter((dataItemId: string | number) => id !== dataItemId) :
      [...expanded[submissionIndex], id];
    this.setState({ expanded });
  };

  /**
   * Filter Change handler
   * 'filter' array should contain Kendo's FilterDescriptors so it can filter treeList by default
   * 'fileNameInputText' contains filtering text that is used to filter grid
   */
  onFilterChange = (event: any) => {
    const fileNameInputText = event.target.value;
    const filter = [{
      operator: 'equal',
      value: 'file',
      field: 'type'
    }, {
      operator: "contains",
      value: fileNameInputText,
      field: 'name'
    }];
    this.setState({ filter, fileNameInputText });
  };

  updateDialogButtons = () => {
    const filesCount = this.getNumberOfSelectedFiles();
    const { filesGridDisplayed } = this.state;
    this.dispatch('updateCancelSubmissionFooter', Position.end, {
      filesCount,
      filesGridDisplayed
    })
  };

  /**
   * Formats file and sets it selected
   * @param submissionIndex
   * @param targetId
   * @param selected
   */
  selectionHandlerFromFilesGrid = (submissionIndex: number, targetId: number, selected: boolean) => {
    const dataItem = {
      type: 'file',
      id: targetId,
      parentId: this.filesGridJobId
    };
    this.addTargetIds(submissionIndex, targetId);
    this.selectionHandler(submissionIndex, (dataItem as TREE_LIST_ITEM), selected);
  };


  /**
   * Handles selection for all types of dataItems in treeList
   */
  selectionHandler = (submissionIndex: number, dataItem: TREE_LIST_ITEM, selected: boolean) => {
    const selectionInfo = [...this.state.selectionInfo];
    let submissionSelectionInfo = selectionInfo[submissionIndex];
    const { type } = dataItem;
    switch (type) {
      case 'firstLevel':
        this.setFirstLevelSelected(submissionSelectionInfo, dataItem, selected);
        break;
      case 'job':
        this.setJobSelected(submissionSelectionInfo, dataItem, selected);
        break;
      case 'file':
        this.setFileSelected(submissionSelectionInfo, dataItem, selected);
        break;
    }
    selectionInfo[submissionIndex] = submissionSelectionInfo;
    this.setState({ selectionInfo }, this.updateDialogButtons);
  };


  /**
   * FirstLevel-type DataItem selection handler
   */
  setFirstLevelSelected = (submissionSelectionInfo: Record<string, JOB_SELECTION_INFO>, dataItem: TREE_LIST_ITEM, selected: boolean) => {
    const jobs = dataItem.subItems;
    jobs.forEach((job: TREE_LIST_ITEM) => {
      this.setJobSelected(submissionSelectionInfo, job, selected)
    })
  };

  /**
   * Job-type DataItem selection handler
   * @param submissionSelectionInfo
   * @param dataItem
   * @param selected
   */
  setJobSelected = (submissionSelectionInfo: Record<string, JOB_SELECTION_INFO>, dataItem: TREE_LIST_ITEM, selected: boolean) => {
    const jobInfo = submissionSelectionInfo[dataItem.id];
    jobInfo.targetsSelected = [];
    jobInfo.jobSelected = selected;
  };

  /**
   *  Change handler of selectAll checkbox on grid
   */
  setJobSelectedFromGrid = (submissionIndex: number, event: CheckboxChangeEvent) => {
    const selectionInfo = [...this.state.selectionInfo];
    const jobInfo = selectionInfo[submissionIndex][this.filesGridJobId];
    jobInfo.targetsSelected = [];
    jobInfo.jobSelected = event.value;
    this.setState({ selectionInfo }, this.updateDialogButtons);
  };

  /**
   * File-type DataItem selection handler
   * @param submissionSelectionInfo
   * @param dataItem
   * @param selected
   */
  setFileSelected = (submissionSelectionInfo: Record<string, JOB_SELECTION_INFO>, dataItem: TREE_LIST_ITEM, selected: boolean) => {
    const jobId = dataItem.parentId;
    const jobInfo = submissionSelectionInfo[(jobId as string)];
    if (selected) {
      if (!_.includes(jobInfo.targetsSelected, dataItem.id)) {
        jobInfo.targetsSelected.push(dataItem.id);
      }
    } else {
      if (_.isEmpty(jobInfo.targetsSelected) && jobInfo.jobSelected) {
        jobInfo.jobTargets.forEach((targetId: number) => {
          if (targetId !== dataItem.id) jobInfo.targetsSelected.push(targetId);
        });
      }
      jobInfo.jobSelected = false;
      jobInfo.targetsSelected = jobInfo.targetsSelected.filter((id: number) => id !== dataItem.id);
    }
  };

  /**
   * Set active tab state
   * @param tab
   */
  setActiveTab = (tab: string) => {
    this.setState({
      activeTabLanguages: tab === 'language',
      activeTab: tab
    });
  }

  /**
   * Handles click on "show more files" link, updates props for files grid and sets it's visibility state to true
   * @param submissionIndex
   * @param dataItem
   */
  showMoreFilesHandler = (submissionIndex: number, dataItem: DATA_TREE_OBJECT) => {
    const { jobs, submissionId } = this.submissions[submissionIndex];
    const jobId = dataItem.parentId;
    const job = _.find(jobs, ({ batchName, languageLocale }: any) => this.createJobId(batchName, languageLocale) === jobId);
    if (job) {
      const { batchName, languageLocale, languageValue, filesCount: total } = job;
      const filesGridProps = {
        params: {
          batchNameValues: [batchName],
          targetLanguageValues: [languageLocale],
          phaseStatusValues: ["READY", "IN_PROCESS", "IN_PROGRESS", "UPLOADED"],
          submissionId,
          size: 200,
          index: 0
        },
        jobDetails: {
          batchName,
          languageLocale,
          languageValue,
          total
        }
      };
      this.filesGridJobId = (jobId as string);
      this.filesGridSubmissionIndex = submissionIndex;
      this.setState({
        filesGridDisplayed: true,
        filesGridProps
      }, this.updateDialogButtons)
    }
  };

  /**
   * Back button handler for FilesGrid. Hides FilesGrid and displays TreeList
   */
  switchBackToTreeList = () => this.setState({ filesGridDisplayed: false }, this.updateDialogButtons);

  /**
   * File name filter renderer
   */
  renderFileNameFilter = (): ReactElement<HTMLTableRowElement> => {
    const { fileNameInputText } = this.state;
    return (
      <div className={'tp-cancel-submission-file-name-filter'}>
        <PuzzleInput
          searchField
          id={`${this.moduleName}-fileName`}
          placeholder={this.translate('emptyFormLabel')}
          onChange={this.onFilterChange}
          value={fileNameInputText}
        />
      </div>
    )
  };

  renderBody() {
    const {
      activeTabLanguages,
      activeTab,
      selectionInfo,
      filesGridDisplayed,
      filesGridProps,
      fileNameInputText,
      filter,
      expanded
    } = this.state;

    let filesGrid = filesGridDisplayed ?
      <CancelSubmissionFilesGrid
        addTargetIds={this.addTargetIds.bind(this, this.filesGridSubmissionIndex)}
        initProps={filesGridProps}
        fileNameInputText={fileNameInputText}
        getBack={this.switchBackToTreeList}
        selectionInfo={selectionInfo[this.filesGridSubmissionIndex][this.filesGridJobId]}
        onSelectionChange={this.selectionHandlerFromFilesGrid.bind(this, this.filesGridSubmissionIndex)}
      /> : null

    let filesOnlyGrid = !filesGridDisplayed ?
      <>
        {!!this.submissions.length &&
          this.submissions.map((submission: SUBMISSION_DATA, index: number) => {
            const data = activeTabLanguages ? this.dataTreeForLanguage[index] : this.dataTreeForBatch[index];
            return <CancelSelectedOnlySubmissionSection
              key={'CancelSelectedOnlySubmission-' + index}
              data={data}
              setSelected={this.selectionHandler.bind(this, index)}
              selectionInfo={selectionInfo[index]}
              activeTabLanguages={activeTabLanguages}
              showMoreFiles={this.showMoreFilesHandler.bind(this, index)}
              filter={filter}
              expanded={expanded[index]}
              onExpandChange={this.onExpandChange.bind(this, index)}
              onExpandBtnClick={() => {
                this.setState({ expanded: !this.state.expanded })
              }}
            />
          })
        }
      </> : null

    return (
      <>
        <div className={"tp-cancel-submission-content"}>
          <PuzzleToggle
            id={this.moduleName}
            switcherLabel={this.translate('SRL-switcherLabel')}
            setActiveTab={this.setActiveTab}
            activeTab={activeTab}
            checked={activeTab === 'batch'}
            switcherOptions={[{
              name: 'language',
              locale: this.translate('SRL-firstOptionLabel')
            }, {
              name: 'batch',
              locale: this.translate('SRL-secondOptionLabel')
            }]}
          />
          <div className={'tp-cancel-submission-topbar-wrap'}>{this.renderFileNameFilter()}</div>
          <Push
            className={`${filesGridDisplayed ? 'files' : '!k-display-none'}`}
            direction={'left'}>
            {filesGrid}
          </Push>
          <Push
            className={`${!filesGridDisplayed ? 'subs' : '!k-display-none'}`}
            direction={'right'}>
            {filesOnlyGrid}
          </Push>
        </div>
      </>
    );
  }
}

export default CancelSelectedOnly;
